import { Component, ElementRef, ViewChild } from "@angular/core";
import { GeneralPortfolioComponent } from "../general-portfolio/general-portfolio.component";
import { HC_LEGEND_STYLE, HC_COLORS } from "../../charts/highcharts-shared";
import moment from "moment";
import { PortfolioModel } from "../../shared/models/portfolio.model";
import {
  ColumnType,
  CimDatatableColumn,
} from "../../components/cim-datatable/cim-datatable.component";

import { forkJoin } from "rxjs";
import { MaterialModule } from "src/app/app.materials";
import { PortfolioReturnBarChartComponent } from "src/app/charts/portfolio-return-bar-chart/portfolio-return-bar-chart.component";
import { LineChartComponent } from "src/app/components/line-chart/line-chart.component";
import { RiskContributionColumnChartComponent } from "src/app/charts/risk-contribution-column-chart/risk-contribution-column-chart.component";

import { RouterModule } from "@angular/router";
import { FormsModule } from "@angular/forms";
import { CummulativeReturnChartComponent } from "src/app/charts/cummulative-return-chart/cummulative-return-chart.component";
import { CimDatatableComponent } from "src/app/components/cim-datatable/cim-datatable.component";
import { PdfExportDialogComponent } from "src/app/components/pdf-export-dialog/pdf-export-dialog.component";
import { SparklineComponent } from "src/app/charts/sparkline/sparkline.component";
import {
  HbarListComponent,
  HBarRow,
} from "src/app/charts/hbar-list/hbar-list.component";
import { ChartPlaceholderComponent } from "src/app/components/chart-placeholder/chart-placeholder.component";

interface BenchmarkOption {
  name: string;
  full: string;
  group: string;
  raw: any;
}

interface BenchmarkGroup {
  label: string;
  items: BenchmarkOption[];
}

@Component({
  selector: "app-overview",
  preserveWhitespaces: true,
  templateUrl: "./overview.component.html",
  styleUrls: ["./overview.component.scss"],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    MaterialModule,
    SparklineComponent,
    HbarListComponent,
    CummulativeReturnChartComponent,
    LineChartComponent,
    PortfolioReturnBarChartComponent,
    RiskContributionColumnChartComponent,
    CimDatatableComponent,
    ChartPlaceholderComponent,
  ],
})
export class OverviewComponent extends GeneralPortfolioComponent {
  @ViewChild("cumulativeCard", { static: false }) cumulativeCard!: ElementRef;
  @ViewChild("annualCard", { static: false }) annualCard!: ElementRef;
  @ViewChild("weightsCard", { static: false }) weightsCard!: ElementRef;
  @ViewChild("constituentsCard", { static: false })
  constituentsCard!: ElementRef;
  @ViewChild("riskContribCard", { static: false }) riskContribCard!: ElementRef;

  benchmarkCalculating = false;
  filter: any = { start: null, end: null };
  annualChartType:
    | "annualized_return"
    | "annualized_sharpe"
    | "annualized_std_deviation" = "annualized_return";

  treeMapData: any[] = null;
  volatilityData: any = null;
  drawdownData: any[] = null;
  generalData: any[] = [];
  portfolioData: any[] = [];
  portfolioReturn: any = null;

  riskContributionData: any[] = [];
  benchmarkReturn: any = [];
  evaluationData: any = {};
  marginalRiskContributionData: any[] = [{ data: [] }];
  portfolioLoadedInService: PortfolioModel = null;

  chartsReady = false;

  negativeDataLabelsY = -4;
  positiveDataLabelsY = -20;

  // KPI tiles
  kpis = {
    totalReturn: null as number | null,
    annualizedReturn: null as number | null,
    sharpe: null as number | null,
    maxDrawdown: null as number | null,
    cagrY5: null as number | null,
  };
  benchKpis = {
    totalReturn: null as number | null,
    annualizedReturn: null as number | null,
    sharpe: null as number | null,
    maxDrawdown: null as number | null,
  };

  // Side-stack cards
  valuation = {
    dividendYield: null as number | null,
    pe: null as number | null,
    ps: null as number | null,
    pb: null as number | null,
    avgMarketCap: null as number | null,
    avgEnterpriseValue: null as number | null,
    avgEBITDA: null as number | null,
  };
  risk = {
    volatility: null as number | null,
    var95: null as number | null,
    var99: null as number | null,
    alpha: null as number | null,
    beta: null as number | null,
    treynor: null as number | null,
    calmar: null as number | null,
    trackingError: null as number | null,
    informationRatio: null as number | null,
  };
  riskBench = {
    volatility: null as number | null,
    var95: null as number | null,
    var99: null as number | null,
    calmar: null as number | null,
  };

  // Sparkline series (decimated to a few dozen samples for tiny SVG sparks)
  sparks = {
    total: [] as number[],
    annual: [] as number[],
    sharpe: [] as number[],
    drawdown: [] as number[],
  };

  // Weights vs Volatility hbar lists
  weightsRows: HBarRow[] = [];
  volRows: HBarRow[] = [];
  returnRows: HBarRow[] = [];

  // Settings strip state
  editStart: string | null = null;
  editEnd: string | null = null;
  editBenchmarkName: string = "";
  private originalStart: string | null = null;
  private originalEnd: string | null = null;
  private originalBenchmark: string = "";
  isApplying = false;

  benchmarkGroups: BenchmarkGroup[] = [];
  private benchmarkByName = new Map<string, BenchmarkOption>();
  private readonly assetGroups = [
    "Europe",
    "Asia",
    "World",
    "North America",
    "The Nordics",
  ];

  sharpeChartData: any[] = [];
  hasAnnualData = false;
  annualChartOptions: any = {
    tooltip: {
      // Annual perf chart uses LineChartComponent which mounts via stockChart;
      // stock defaults force `outside: true` and `split: true` on top of the
      // global tooltip styling. Override both so the tooltip box matches the
      // rest of the app.
      outside: false,
      split: false,
      valueDecimals: 2,
      valueSuffix: "",
    },
    navigator: { enabled: false },
    zoom: { enabled: false },
    rangeSelector: { enabled: false },
    scrollbar: { enabled: false },
    chart: {
      type: "column",
      height: 280,
      backgroundColor: "transparent",
      style: { fontFamily: "Inter, system-ui, sans-serif" },
    },
    xAxis: {
      gridLineWidth: 0,
      lineColor: "transparent",
      tickColor: "transparent",
      labels: { style: { color: "var(--text-dim)" } },
    },
    yAxis: {
      opposite: false,
      title: {
        text: "Return",
        style: {
          textTransform: "capitalize",
          color: "var(--text-dim)",
          fontSize: "11px",
          fontWeight: "500",
        },
      },
      gridLineColor: "var(--border)",
      gridLineDashStyle: "ShortDash",
      gridLineWidth: 1,
      minorGridLineWidth: 0,
      maxPadding: 0.12,
      minPadding: 0.08,
      labels: { style: { color: "var(--text-dim)" } },
      plotLines: [
        { value: 0, width: 1, color: "var(--text-faint)", zIndex: 10 },
      ],
      stackLabels: { enabled: true },
    },
    legend: {
      align: "left",
      verticalAlign: "bottom",
      itemDistance: 18,
      symbolRadius: 2,
      symbolHeight: 3,
      symbolWidth: 14,
      squareSymbol: false,
      ...HC_LEGEND_STYLE,
    },
    plotOptions: {
      series: {
        lineWidth: 2,
        groupPadding: 0.12,
        pointPadding: 0.02,
        dataGrouping: { enabled: false },
        dataLabels: {
          valueDecimals: 0,
          format: "{point.y:,.0f}%",
          allowOverlap: false,
        },
        tooltip: {
          pointFormat: "{series.name}: <b>{point.y}%</b>",
        },
      },
      column: {
        borderWidth: 0,
        borderRadius: 3,
        maxPointWidth: 40,
        dataLabels: {
          enabled: true,
          format: "{y:.0f}%",
          inside: false,
          verticalAlign: "top",
          align: "center",
          crop: false,
          overflow: "allow",
          allowOverlap: false,
          padding: 2,
          style: {
            fontSize: "10px",
            fontWeight: "500",
            textOutline: "none",
            color: "var(--text-strong)",
          },
        },
      },
    },
  };

  portfolioConstituentsTableColumns: CimDatatableColumn[] = [
    { id: "company_name", label: "Asset", type: ColumnType.CompanyInfo },
    { id: "type", label: "Class", type: ColumnType.Simple },
    { id: "Weight", label: "Weight", type: ColumnType.Percentage },
    { id: "CAGR", label: "CAGR", type: ColumnType.Percentage },
    { id: "Volatility", label: "Volatility", type: ColumnType.Percentage },
    {
      id: "riskContribution",
      label: "Risk contrib.",
      type: ColumnType.Percentage,
    },
    { id: "maxDrawdown", label: "Max DD", type: ColumnType.Percentage },
    { id: "Sharpe", label: "Sharpe", type: ColumnType.Number2Decimal },
  ];

  isLoaded = false;

  initPage() {
    this.service.portfolio$.subscribe(
      (res) => (this.portfolioLoadedInService = res),
    );
    this.loadBenchmarkOptions();

    if (this.portfolio && this.portfolio.id) {
      this.setupData();
    }
  }

  setupData() {
    if (!this.treeMapData) {
      this.extractTreeMapData();
    }

    if (!this.portfolio.benchmark) {
      // no benchmark selected
    } else {
      this.service.selectBenchmark(this.portfolio.benchmark);
      this.selectedBenchmark = this.portfolio.benchmark;
    }

    forkJoin({
      e: this.service.getBenchmark(
        btoa(this.portfolio.benchmark),
        this.portfolio.id,
      ),
      c: this.service.fetchPerformancePlot(this.portfolio.id),
      v: this.service.fetchEvaluationData(this.portfolio.id),
    }).subscribe((val) => {
      this.service.selectBenchmark(val.e);
      this.selectedBenchmark = val.e;

      this.evaluationData = val.v;
      this.plotData = this.convertToPercentages(val.c);
      this.calculateBenchmark(val.e);
      this.setGeneralData();
      this.updatePageWithBenchmark();
      this.seedEditState();
      this.setupKpis();
      this.setupValuation();
      this.setupRisk();
      this.setupSparks();
      this.setupAnnualFlag();
      this.setupHbars();
      this.isLoaded = true;
      this.afterPlotDataReceived();
    });
  }

  setGeneralData() {
    this.portfolio.symbol_performance.map((e: any) => {
      if (e.Ticker === "Portfolio") {
        this.portfolioData.push({
          CAGR: e.CAGR,
          Volatility: e.Volatility,
          Sharpe: e.Sharpe,
          TrackingError: e.TrackingError,
          Beta: e.Beta,
          Alpha: e.Alpha,
          maxDrawdown: e["Max Drawdown"],
        });
      }

      if (e.Ticker !== "Portfolio" && e.Weight > 0) {
        const item = this.evaluationData.risk_contribution.find(
          (a) => a.Ticker == e.Ticker && a.Portfolio == "baseline_portfolio",
        );
        let riskContribution = 0;
        if (item) {
          riskContribution = item.risk_contribution;
        }

        let companyName = "";
        let type = "";
        if (e.reference_table !== null) {
          companyName = e.reference_table.company_name;
          type = e.reference_table.security_type_equity;
        } else if (e.etf_reference_table !== null) {
          companyName = e.etf_reference_table.name;
          type = "ETF";
        } else if (e.fund_reference_table !== null) {
          companyName = e.fund_reference_table.name;
          type = "FUND";
        } else {
          type = "SHARE";
        }

        this.generalData.push({
          Ticker: e.Ticker,
          Weight: e.Weight,
          CAGR: e.CAGR,
          Return:
            this.treeMapData[0].data.find((a) => a.name == e.Ticker).value /
            100,
          riskContribution: riskContribution,
          company_name: companyName,
          sector:
            e.reference_table !== null ? e.reference_table.sector_fs : "FUND",
          Sharpe: e.Sharpe,
          Volatility: e.Volatility,
          sortino:
            (this.treeMapData[0].data.find((a) => a.name == e.Ticker).value -
              1.5) /
            e.Volatility,
          maxDrawdown: e["Max Drawdown"],
          type,
        });

        this.riskContributionData.push({
          Ticker: companyName,
          riskContribution: riskContribution * 100,
          Weight: e.Weight * 100,
          Return:
            this.treeMapData[0].data.find((a) => a.name == e.Ticker).value *
            e.Weight,
        });

        this.marginalRiskContributionData[0].data.push({
          company: companyName,
          risk: parseFloat((e.Weight * riskContribution * 100).toFixed(2)),
          return: parseFloat((e.Weight * e.totalReturn * 100).toFixed(2)),
        });
      }
    });
  }

  // -- KPI / valuation / risk / sparks / weights computations -------------

  private setupKpis(): void {
    const s = this.portfolio?.performance_summary || {};
    const cum = this.plotData?.cumulative_returns?.Portfolio;
    const portfolioSym = (this.portfolio?.symbol_performance || []).find(
      (e: any) => e.Ticker === "Portfolio",
    );
    const numOrNull = (v: any): number | null =>
      typeof v === "number" && isFinite(v) ? v : null;

    this.kpis.annualizedReturn =
      numOrNull(s["Annualized Return"]?.Portfolio) ??
      numOrNull(portfolioSym?.CAGR) ??
      this.computeAnnualizedFromCum(cum);
    this.kpis.sharpe =
      numOrNull(s["Annualized Sharpe (Rf=0%)"]?.Portfolio) ??
      numOrNull(s["Sharpe Ratio"]?.Portfolio) ??
      numOrNull(s["Sharpe"]?.Portfolio) ??
      numOrNull(portfolioSym?.Sharpe);
    this.kpis.maxDrawdown =
      numOrNull(portfolioSym?.["Max Drawdown"]) ??
      numOrNull(s["Max Drawdown"]?.Portfolio);

    if (Array.isArray(cum) && cum.length > 1) {
      const first = cum[0][1];
      const last = cum[cum.length - 1][1];
      this.kpis.totalReturn = first ? (last - first) / first : null;
      const ref5 = cum.slice(-252 * 5)[0]?.[1];
      if (ref5) this.kpis.cagrY5 = Math.pow(last / ref5, 1 / 5) - 1;
    }
    if (this.kpis.maxDrawdown === null && Array.isArray(cum)) {
      this.kpis.maxDrawdown = this.computeMaxDrawdown(cum);
    }

    if (this.benchmark) {
      const bcum = this.benchmark.cumulative_returns || [];
      if (Array.isArray(bcum) && bcum.length > 1) {
        const first = bcum[0]?.value;
        const last = bcum[bcum.length - 1]?.value;
        this.benchKpis.totalReturn = first ? (last - first) / first : null;
      }
      const perfAnn = this.benchmark.perfAnn || [];
      if (perfAnn.length) {
        const avgReturn =
          perfAnn.reduce((a: number, b: any) => a + (b.return || 0), 0) /
          perfAnn.length;
        this.benchKpis.annualizedReturn = avgReturn / 100;
        const avgSharpe =
          perfAnn.reduce((a: number, b: any) => a + (b.sharpe || 0), 0) /
          perfAnn.length;
        this.benchKpis.sharpe = avgSharpe;
      }
      this.benchKpis.maxDrawdown = this.benchmark.maxDrawdown ?? null;
    } else {
      this.benchKpis = {
        totalReturn: null,
        annualizedReturn: null,
        sharpe: null,
        maxDrawdown: null,
      };
    }
  }

  private setupValuation(): void {
    const s = this.portfolio?.performance_summary || {};
    const pick = (key: string): number | null => {
      const row = s[key];
      if (!row) return null;
      const val = row.Portfolio;
      return typeof val === "number" && isFinite(val) ? val : null;
    };
    this.valuation = {
      dividendYield: pick("Dividend Yield"),
      pe: pick("P/E"),
      ps: pick("P/S"),
      pb: pick("P/B"),
      avgMarketCap: pick("Market Value"),
      avgEnterpriseValue: pick("Enterprise Value"),
      avgEBITDA: pick("EBITDA"),
    };
  }

  private setupRisk(): void {
    const s = this.portfolio?.performance_summary || {};
    const portfolioSym = (this.portfolio?.symbol_performance || []).find(
      (e: any) => e.Ticker === "Portfolio",
    );
    const numOrNull = (v: any): number | null =>
      typeof v === "number" && isFinite(v) ? v : null;
    // PF vol uses the same formula as the Benchmark column in the
    // Risk snapshot (daily returns -> sample std -> sqrt(252)) over the
    // user's applied backtest window. Falls back to the R precomputed
    // value only when plotData.cumulative_returns isn't available yet.
    const portfolioStartMs = this.portfolio?.start_date
      ? new Date(this.portfolio.start_date).valueOf()
      : null;
    const portfolioEndMs = this.portfolio?.end_date
      ? new Date(this.portfolio.end_date).valueOf()
      : null;
    const pfCum: any[] = this.plotData?.cumulative_returns?.Portfolio || [];
    if (
      pfCum.length > 1 &&
      portfolioStartMs !== null &&
      portfolioEndMs !== null
    ) {
      const windowedPf = pfCum
        .filter((d: any) => d[0] >= portfolioStartMs && d[0] <= portfolioEndMs)
        .map((d: any) => ({ date: d[0], value: d[1] }));
      this.risk.volatility = this.annualizedVolFromCumulative(windowedPf);
    } else {
      this.risk.volatility =
        numOrNull(portfolioSym?.Volatility) ??
        numOrNull(s["Annualized Std Dev"]?.Portfolio);
    }
    this.risk.var95 =
      numOrNull(s["VaR"]?.Portfolio) ?? numOrNull(s["VaR 95"]?.Portfolio);
    this.risk.var99 =
      numOrNull(s["VaR99"]?.Portfolio) ?? numOrNull(s["VaR 99"]?.Portfolio);
    this.risk.alpha = numOrNull(portfolioSym?.Alpha);
    this.risk.beta = numOrNull(portfolioSym?.Beta);
    this.risk.trackingError = numOrNull(portfolioSym?.TrackingError);

    const annReturn =
      numOrNull(s["Annualized Return"]?.Portfolio) ??
      numOrNull(portfolioSym?.CAGR) ??
      0;
    let benchAnn = 0;
    const perfAnn = this.benchmark?.perfAnn;
    if (Array.isArray(perfAnn) && perfAnn.length) {
      const last = perfAnn[perfAnn.length - 1];
      if (last && typeof last.return === "number") benchAnn = last.return / 100;
    }
    this.risk.informationRatio =
      this.risk.trackingError && this.risk.trackingError > 0
        ? (annReturn - benchAnn) / this.risk.trackingError
        : null;

    this.risk.treynor =
      this.risk.beta && this.kpis.annualizedReturn !== null
        ? this.kpis.annualizedReturn / this.risk.beta
        : null;

    this.risk.calmar =
      this.kpis.maxDrawdown && this.kpis.annualizedReturn !== null
        ? this.kpis.annualizedReturn / this.kpis.maxDrawdown
        : null;

    // Derive benchmark risk stats from cumulative_returns with the same
    // daily-returns -> sample std -> sqrt(252) formula, windowed to the
    // same [portfolio.start_date, portfolio.end_date] interval used above
    // for the PF column. Reuses the portfolioStartMs/portfolioEndMs values
    // declared earlier.
    const cumulativeReturns: any[] =
      this.benchmark?.cumulative_returns || [];
    const windowedCumulative =
      portfolioStartMs !== null && portfolioEndMs !== null
        ? cumulativeReturns.filter(
            (d: any) => d.date >= portfolioStartMs && d.date <= portfolioEndMs,
          )
        : cumulativeReturns;
    this.riskBench.volatility =
      this.annualizedVolFromCumulative(windowedCumulative);

    // VaR and Calmar stay on the daily-frequency series so the percentiles
    // and drawdown granularity match the existing behavior (only the
    // annualization-factor mismatch on vol is the bug the PO flagged).
    const dailyReturnsRaw: any[] = this.benchmark?.monthly_returns || [];
    const windowedDaily =
      portfolioStartMs !== null && portfolioEndMs !== null
        ? dailyReturnsRaw.filter(
            (r: any) => r.date >= portfolioStartMs && r.date <= portfolioEndMs,
          )
        : dailyReturnsRaw;
    const dailyReturns: number[] = windowedDaily.map((r: any) => r.r);
    if (dailyReturns.length > 1) {
      const sorted = [...dailyReturns].sort((a, b) => a - b);
      this.riskBench.var95 = sorted[Math.floor(sorted.length * 0.05)] ?? null;
      this.riskBench.var99 = sorted[Math.floor(sorted.length * 0.01)] ?? null;

      if (this.benchKpis.annualizedReturn !== null) {
        let cumValue = 1;
        let peak = 1;
        let benchMaxDD = 0;
        for (const r of dailyReturns) {
          cumValue *= 1 + r;
          if (cumValue > peak) peak = cumValue;
          const dd = (cumValue - peak) / peak;
          if (dd < benchMaxDD) benchMaxDD = dd;
        }
        this.riskBench.calmar =
          benchMaxDD !== 0
            ? this.benchKpis.annualizedReturn / benchMaxDD
            : null;
      }
    }
  }

  private setupSparks(): void {
    const cum = this.plotData?.cumulative_returns?.Portfolio;
    if (Array.isArray(cum) && cum.length) {
      this.sparks.total = this.sampleSeries(
        cum.map((p: any) => p[1]),
        36,
      );
      this.sparks.drawdown = this.computeDrawdownSpark(cum, 36);
    }
    const ann = this.plotData?.annualized_return?.Portfolio;
    if (Array.isArray(ann) && ann.length) {
      this.sparks.annual = this.sampleSeries(
        ann.map((p: any) => p[1]),
        24,
      );
    }
    const sh = this.plotData?.annualized_sharpe?.Portfolio;
    if (Array.isArray(sh) && sh.length) {
      this.sparks.sharpe = this.sampleSeries(
        sh.map((p: any) => p[1]),
        24,
      );
    }
  }

  // Annualized benchmark vol over a single calendar year. Shares the formula
  // with riskBench.volatility (Backtest) and computeBenchmarkVolForPortfolio-
  // Timespan (Optimization EF dot): daily returns from cumulative_returns
  // -> sample variance (N-1) -> sqrt(variance * 252). Returns percentage
  // points so the value drops straight into the chart's y axis (which
  // formats with "%" suffix on the perfAnn-based bars). The current
  // calendar year naturally uses only the days passed so far -- the helper
  // is window-agnostic.
  private computeBenchmarkAnnualVolPercent(year: number | string): number {
    const cumulative = this.benchmark?.cumulative_returns;
    if (!Array.isArray(cumulative) || cumulative.length < 2) return 0;
    const yearStart = moment.utc(`${year}-01-01`).unix() * 1000;
    const yearEnd = moment.utc(`${year}-12-31T23:59:59`).unix() * 1000;
    const inYear = cumulative.filter(
      (d: any) => d.date >= yearStart && d.date <= yearEnd,
    );
    return this.annualizedVolFromCumulative(inYear) * 100;
  }

  // Same formula for the Portfolio bar so the two series in the Annual
  // performance chart agree by construction (only the window differs).
  // Reads from plotData.cumulative_returns.Portfolio which is the [date,
  // value] tuple shape used across the overview page.
  private computePfAnnualVolPercent(year: number | string): number {
    const cum: any[] = this.plotData?.cumulative_returns?.Portfolio;
    if (!Array.isArray(cum) || cum.length < 2) return 0;
    const yearStart = moment.utc(`${year}-01-01`).unix() * 1000;
    const yearEnd = moment.utc(`${year}-12-31T23:59:59`).unix() * 1000;
    const inYear = cum
      .filter((d: any) => d[0] >= yearStart && d[0] <= yearEnd)
      .map((d: any) => ({ date: d[0], value: d[1] }));
    return this.annualizedVolFromCumulative(inYear) * 100;
  }

  // Calendar-year period return ("Dec 31 -> Dec 31" per the PO) computed
  // identically for both series so the Annual chart bars line up. Partial
  // first/last years use first-of-series / last-available as the missing
  // anchor, giving a partial-period return rather than dropping the bar.
  private computePfAnnualReturnPercent(year: number | string): number {
    const cum: any[] = this.plotData?.cumulative_returns?.Portfolio;
    if (!Array.isArray(cum) || cum.length < 2) return 0;
    const cumObj = cum.map((d: any) => ({ date: d[0], value: d[1] }));
    return this.periodReturnInYearFromCumulative(cumObj, Number(year)) * 100;
  }

  private computeBenchmarkAnnualReturnPercent(year: number | string): number {
    const cum = this.benchmark?.cumulative_returns;
    if (!Array.isArray(cum) || cum.length < 2) return 0;
    return this.periodReturnInYearFromCumulative(cum, Number(year)) * 100;
  }

  // Sharpe = period_return% / annualized_vol%. For a full calendar year this
  // is the standard (Rf=0) Sharpe; for partial first/last years the period
  // return is mixed with an annualized vol, so the value is slightly
  // attenuated -- acceptable because the PO wants the same formula on both
  // series and consistent date anchors (Dec 31 -> Dec 31) more than
  // perfect time-scale alignment on partial years.
  private computePfAnnualSharpe(year: number | string): number {
    const ret = this.computePfAnnualReturnPercent(year);
    const vol = this.computePfAnnualVolPercent(year);
    return vol > 0 ? ret / vol : 0;
  }

  private computeBenchmarkAnnualSharpe(year: number | string): number {
    const ret = this.computeBenchmarkAnnualReturnPercent(year);
    const vol = this.computeBenchmarkAnnualVolPercent(year);
    return vol > 0 ? ret / vol : 0;
  }

  private setupAnnualFlag(): void {
    const candidates: Array<
      "annualized_return" | "annualized_sharpe" | "annualized_std_deviation"
    > = ["annualized_return", "annualized_sharpe", "annualized_std_deviation"];
    this.hasAnnualData = candidates.some(
      (k) =>
        Array.isArray(this.plotData?.[k]?.Portfolio) &&
        this.plotData[k].Portfolio.length > 0,
    );
  }

  private setupHbars(): void {
    const top = [...(this.generalData || [])]
      .sort((a, b) => (b.Weight || 0) - (a.Weight || 0))
      .slice(0, 10);
    this.weightsRows = top.map((r) => ({
      label: r.Ticker,
      value: r.Weight || 0,
    }));
    this.volRows = top.map((r) => ({
      label: r.Ticker,
      value: r.Volatility || 0,
    }));
    this.returnRows = top.map((r) => ({
      label: r.Ticker,
      value: r.Return || 0,
    }));
  }

  private computeAnnualizedFromCum(cum: any[] | undefined): number | null {
    if (!Array.isArray(cum) || cum.length < 2) return null;
    const first = cum[0];
    const last = cum[cum.length - 1];
    const startVal = first?.[1];
    const endVal = last?.[1];
    const startTs = first?.[0];
    const endTs = last?.[0];
    if (!startVal || !endVal || !startTs || !endTs || endTs <= startTs)
      return null;
    const years = (endTs - startTs) / (365.25 * 24 * 3600 * 1000);
    if (years <= 0) return null;
    return Math.pow(endVal / startVal, 1 / years) - 1;
  }

  private computeMaxDrawdown(cum: any[]): number | null {
    if (!Array.isArray(cum) || cum.length < 2) return null;
    let peak = cum[0][1];
    let maxDD = 0;
    for (const p of cum) {
      const v = p[1];
      if (v > peak) peak = v;
      const dd = (v - peak) / peak;
      if (dd < maxDD) maxDD = dd;
    }
    return maxDD;
  }

  private computeDrawdownSpark(cum: any[], n: number): number[] {
    const out: number[] = [];
    let peak = cum[0][1];
    for (const p of cum) {
      const v = p[1];
      if (v > peak) peak = v;
      out.push(((v - peak) / peak) * 100);
    }
    return this.sampleSeries(out, n);
  }

  private sampleSeries(values: number[], n: number): number[] {
    if (!values.length) return [];
    if (values.length <= n) return values.slice();
    const step = values.length / n;
    const result: number[] = [];
    for (let i = 0; i < n; i++) result.push(values[Math.floor(i * step)]);
    result.push(values[values.length - 1]);
    return result;
  }

  // -- Settings strip -----------------------------------------------------

  private seedEditState(): void {
    this.originalStart = this.portfolio?.start_date ?? null;
    this.originalEnd = this.portfolio?.end_date ?? null;
    this.originalBenchmark = this.portfolio?.benchmark ?? "";
    this.editStart = this.originalStart;
    this.editEnd = this.originalEnd;
    this.editBenchmarkName = this.originalBenchmark;
  }

  private loadBenchmarkOptions(): void {
    Promise.resolve(this.service.getBenchmarks())
      .then((obs: any) => {
        obs?.subscribe?.((groups: any[]) => {
          if (!Array.isArray(groups)) return;
          this.benchmarkGroups = groups
            .filter((g: any) => this.assetGroups.includes(g?.group))
            .map((g: any) => ({
              label: g.group,
              items: (g.items || []).map((b: any) => {
                const opt: BenchmarkOption = {
                  name: b.name,
                  full: b.name_full || b.name,
                  group: g.group,
                  raw: b,
                };
                this.benchmarkByName.set(b.name, opt);
                return opt;
              }),
            }))
            .filter((g) => g.items.length > 0);
        });
      })
      .catch(() => {});
  }

  get hasSettingsChanges(): boolean {
    if (!this.portfolio) return false;
    return (
      this.editStart !== this.originalStart ||
      this.editEnd !== this.originalEnd ||
      this.editBenchmarkName !== this.originalBenchmark
    );
  }

  get canApply(): boolean {
    return (
      !!this.portfolio?.id &&
      !!this.editStart &&
      !!this.editEnd &&
      !this.isApplying &&
      this.hasSettingsChanges
    );
  }

  onResetSettings(): void {
    this.editStart = this.originalStart;
    this.editEnd = this.originalEnd;
    this.editBenchmarkName = this.originalBenchmark;
  }

  onApply(): void {
    if (!this.portfolio?.id || !this.editStart || !this.editEnd) {
      this.service.notificationService?.open?.(
        "Please fill the start and end dates.",
      );
      return;
    }
    if (this.editStart >= this.editEnd) {
      this.service.notificationService?.open?.(
        "End date must be after start date.",
      );
      return;
    }
    this.portfolio.start_date = this.editStart;
    this.portfolio.end_date = this.editEnd;
    if (
      this.editBenchmarkName &&
      this.editBenchmarkName !== this.originalBenchmark
    ) {
      this.portfolio.benchmark = this.editBenchmarkName;
      this.benchmarkCalculating = true;
      const opt = this.benchmarkByName.get(this.editBenchmarkName);
      if (opt) {
        this.selectedBenchmark = opt.raw;
      }
    }
    this.isApplying = true;
    this.portfolio.needReload = true;
    this.handleReturnCalculationRequest("Your backtest is running");
    // The polling mechanism inherited from GeneralPortfolioComponent
    // resets `isApplying` once the recalculation completes. Until then,
    // we just block the strip via `isApplying`.
    setTimeout(() => {
      this.isApplying = false;
    }, 1500);
  }

  setAnnualMetric(
    m: "annualized_return" | "annualized_sharpe" | "annualized_std_deviation",
  ): void {
    this.annualChartType = m;
    this.initAnnualSharpeChart();
  }

  // -- Existing flows kept intact -----------------------------------------

  checkSelectedBenchmark() {
    if (this.benchmark) {
      this.selectedBenchmark = this.portfolio.benchmark = this.benchmark;
      this.updatePageWithBenchmark();
    }
  }

  dayOfTheYear() {
    const date: Date = new Date();
    return (
      (Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) -
        Date.UTC(date.getFullYear(), 0, 0)) /
      24 /
      60 /
      60 /
      1000
    );
  }

  afterPlotDataReceived() {
    const portfolioStart = new Date(this.portfolio.start_date).valueOf();
    const portfolioEnd = new Date(this.portfolio.end_date).valueOf();
    const portfolioCum = this.plotData?.cumulative_returns?.Portfolio;
    const dailyReturns = this.plotData?.daily_returns?.Portfolio;
    const annualized = this.plotData?.annualized_return?.Portfolio;

    this.drawdownData = this.calculateDrawdown(
      this.plotData.cumulative_returns,
      portfolioStart,
    );

    if (!portfolioCum?.length) {
      this.initVolatilityChart();
      this.initAnnualSharpeChart();
      this.chartsReady = true;
      return;
    }

    const copy = structuredClone(
      portfolioCum.filter(function (e) {
        return e[0] >= +portfolioStart && e[0] <= portfolioEnd;
      }),
    );
    const d = new Date(portfolioEnd);
    const dateOfTheYear = moment.utc(d).startOf("year").unix() * 1000;
    const last_return_date = portfolioCum.slice(-1)[0][0];
    const last_return_date_1month =
      moment.utc(last_return_date).subtract(1, "month").unix() * 1000;

    // Same defensive pattern as `updatePageWithBenchmark`: short / new-year
    // portfolios lack the historical anchors. Series here is a [date, value]
    // tuple list (not {date,value} objects like the benchmark series).
    const pctChange = (latest: number, anchor: any): number =>
      anchor && anchor[1] !== 0
        ? ((latest - anchor[1]) / anchor[1]) * 100
        : NaN;
    const cagrFromAnchor = (latest: number, anchor: any, years: number): number =>
      anchor && anchor[1] !== 0 && years > 0
        ? (Math.pow(latest / anchor[1], 1 / years) - 1) * 100
        : NaN;

    const portfolioLast = portfolioCum.slice(-1)[0];
    const mntAnchor = portfolioCum.filter(function (e) {
      return e[0] >= last_return_date_1month;
    })[0];

    const baseEntry = copy.length >= 1 ? copy[0] : null;
    const copyLast = copy.length >= 1 ? copy[copy.length - 1] : null;
    const ytdAnchor = portfolioCum
      .filter(function (e) {
        return e[0] < dateOfTheYear;
      })
      .slice(-1)[0];
    const anchorAtTradingDaysBack = (yearsBack: number) =>
      portfolioCum.length >= 252 * yearsBack
        ? portfolioCum.slice(-252 * yearsBack)[0]
        : undefined;

    this.portfolioReturn = {
      mnt: pctChange(portfolioLast[1], mntAnchor),
      totalReturn:
        baseEntry && copyLast && baseEntry[1] !== 0
          ? ((copyLast[1] - baseEntry[1]) / baseEntry[1]) * 100
          : NaN,
      avgAnn: annualized?.length
        ? annualized.reduce((p, c) => p + c[1], 0) / annualized.length
        : NaN,
      ytd: pctChange(portfolioLast[1], ytdAnchor),
      cagrY1: dailyReturns?.length
        ? dailyReturns.slice(-365).reduce((sum, ret) => sum + ret[1], 0)
        : NaN,
      cagrY2: cagrFromAnchor(portfolioLast[1], anchorAtTradingDaysBack(2), 2),
      cagrY3: cagrFromAnchor(portfolioLast[1], anchorAtTradingDaysBack(3), 3),
      cagrY5: cagrFromAnchor(portfolioLast[1], anchorAtTradingDaysBack(5), 5),
      cagrY10: cagrFromAnchor(portfolioLast[1], anchorAtTradingDaysBack(10), 10),
    };

    this.initVolatilityChart();
    this.initAnnualSharpeChart();
    this.chartsReady = true;
  }

  initVolatilityChart() {
    this.volatilityData = [];
    if (this.plotData && this.plotData.rolling_std_deviation) {
      this.volatilityData = {
        title: "",
        series: [
          {
            name: "Portfolio",
            type: "line",
            data: this.plotData.rolling_std_deviation["Portfolio"],
          },
        ],
      };
    }
  }

  initAnnualSharpeChart() {
    const portfolioStart = new Date(this.portfolio.start_date).valueOf();
    const portfolioEnd = new Date(this.portfolio.end_date).valueOf();
    this.sharpeChartData = [];

    // Build the year list from the portfolio window directly instead of
    // driving off perfAnn / plotData.annualized_*. The R perfAnn table is
    // only populated for completed calendar years (so 2026 was missing
    // mid-year on the benchmark series), and the Jan 1 anchor for the
    // benchmark filter dropped the first year entirely whenever
    // start_date > Jan 1 (the bug the PO reproduced with 2010-01-02). With
    // a year-range iteration we get one slot per overlapping calendar
    // year, including the partial first and current years.
    const startYear = new Date(portfolioStart).getUTCFullYear();
    const endYear = new Date(portfolioEnd).getUTCFullYear();
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);

    // Shared per-year compute. Both Portfolio and Benchmark go through
    // these closures so the "Dec 31 -> Dec 31" anchoring and the formula
    // are identical -- only the underlying cumulative_returns series
    // differs.
    const isVolMode = this.annualChartType === "annualized_std_deviation";
    const isReturnMode = this.annualChartType === "annualized_return";
    const compute = (
      year: number,
      vol: (y: number | string) => number,
      ret: (y: number | string) => number,
      sharpe: (y: number | string) => number,
    ): number => {
      if (isVolMode) return vol(year);
      if (isReturnMode) return ret(year);
      return sharpe(year);
    };
    const dataLabelsFor = (y: number) => ({
      y: y >= 0 ? this.positiveDataLabelsY : this.negativeDataLabelsY,
    });

    this.sharpeChartData.push({
      name: "Portfolio",
      data: years.map((year) => {
        const x = moment.utc(`${year}-01-01`).unix() * 1000;
        const y = compute(
          year,
          (yr) => this.computePfAnnualVolPercent(yr),
          (yr) => this.computePfAnnualReturnPercent(yr),
          (yr) => this.computePfAnnualSharpe(yr),
        );
        return { x, y, dataLabels: dataLabelsFor(y) };
      }),
      type: "column",
    });

    if (this.benchmark) {
      this.sharpeChartData.push({
        name: this.benchmark.index_name_short ?? "Benchmark",
        data: years.map((year) => {
          const x = moment.utc(`${year}-01-01`).unix() * 1000;
          const y = compute(
            year,
            (yr) => this.computeBenchmarkAnnualVolPercent(yr),
            (yr) => this.computeBenchmarkAnnualReturnPercent(yr),
            (yr) => this.computeBenchmarkAnnualSharpe(yr),
          );
          return { x, y, dataLabels: dataLabelsFor(y) };
        }),
        type: "column",
      });
    }

    if (this.annualChartType === "annualized_return") {
      if (
        this.portfolioReturn &&
        this.portfolioReturn.ytd &&
        this.sharpeChartData[0].data.length
      ) {
        (
          this.sharpeChartData[0].data[
            this.sharpeChartData[0].data.length - 1
          ] as any
        ).y = this.portfolioReturn.ytd;
      }
      if (
        this.benchmarkReturn &&
        this.benchmarkReturn.ytd &&
        this.sharpeChartData[1]?.data?.length
      ) {
        (
          this.sharpeChartData[1].data[
            this.sharpeChartData[1].data.length - 1
          ] as any
        ).y = this.benchmarkReturn.ytd;
      }
      this.annualChartOptions.yAxis.title.text = "Return";
      this.annualChartOptions.yAxis.labels = {
        ...(this.annualChartOptions.yAxis.labels || {}),
        format: "{text}%",
      };
      this.annualChartOptions.plotOptions.series.tooltip =
        "{series.name}: <b>{point.y}%</b>";
      this.annualChartOptions.tooltip.valueSuffix = "%";
      this.annualChartOptions.plotOptions.column.dataLabels.format =
        "{y:,.0f}%";
    } else if (this.annualChartType === "annualized_std_deviation") {
      this.annualChartOptions.yAxis.title.text = "Volatility";
      this.annualChartOptions.yAxis.labels = {
        ...(this.annualChartOptions.yAxis.labels || {}),
        format: "{text}%",
      };
      this.annualChartOptions.plotOptions.series.tooltip =
        "{series.name}: <b>{point.y}%</b>";
      this.annualChartOptions.tooltip.valueSuffix = "%";
      this.annualChartOptions.plotOptions.column.dataLabels.format =
        "{y:,.0f}%";
    } else {
      this.annualChartOptions.yAxis.title.text = "Sharpe Ratio";
      this.annualChartOptions.yAxis.labels = {
        ...(this.annualChartOptions.yAxis.labels || {}),
        format: "{text}",
      };
      this.annualChartOptions.plotOptions.series.tooltip =
        "{series.name}: <b>{point.y}</b>";
      this.annualChartOptions.tooltip.valueSuffix = "";
      this.annualChartOptions.plotOptions.column.dataLabels.format = "{y:,.2f}";
    }

    // Scale group/point spacing to the year count so paired bars (Portfolio +
    // Benchmark) stay visually grouped — wide year groups would otherwise leave
    // each bar sitting at its slot center with a large gap between them.
    const yearCount = this.sharpeChartData[0]?.data?.length ?? 0;
    let groupPadding: number;
    if (yearCount <= 2) groupPadding = 0.42;
    else if (yearCount <= 5) groupPadding = 0.32;
    else if (yearCount <= 10) groupPadding = 0.2;
    else groupPadding = 0.12;
    this.annualChartOptions.plotOptions.series.groupPadding = groupPadding;
    this.annualChartOptions.plotOptions.series.pointPadding = 0;
  }

  setColor(value, min, max) {
    let color: string = HC_COLORS.pos;
    let percent = 100;
    if (value < 0) {
      color = HC_COLORS.neg;
      percent = (Math.abs(value) / Math.abs(min)) * 100;
    } else {
      percent = (Math.abs(value) / Math.abs(max)) * 100;
    }
    return this.util.shadeColor(color, 100 - percent);
  }

  setColors(list, min, max) {
    Object.entries(list).map(([key, data]: [string, any]) => {
      list[key].color = this.setColor(data.realValue, min, max);
    });
  }

  extractTreeMapData() {
    if (this.portfolio.treemap_plot && this.portfolio.treemap_plot.length) {
      const return_data = [];
      const std_data = [];

      let minReturn = 99999;
      let maxReturn = -99999;
      let minVolatility = 99999;
      let maxVolatility = -99999;

      Object.entries(this.portfolio.treemap_plot).map(
        ([key, data]: [string, any]) => {
          const company =
            this.portfolio.security_overviews[data.Ticker] || null;
          const companyName = company
            ? company.company_name
            : this.getFundName(data.Ticker);
          const type = company ? company.security_type_equity : "";
          if (data.historical_returns) {
            if (minReturn > +data.historical_returns)
              minReturn = +data.historical_returns;
            if (maxReturn < +data.historical_returns)
              maxReturn = +data.historical_returns;
            return_data[data.Ticker] = {
              name: data.Ticker,
              company: companyName,
              value: +(data.historical_returns * 100).toFixed(2),
              realValue: +(data.historical_returns * 100).toFixed(2),
              country: data.country_hq_name,
              industry: data.industry_fs,
              sector: data.sector_fs,
              type,
            };
          }
          if (data.volatility) {
            if (minVolatility > +data.volatility)
              minVolatility = +data.volatility;
            if (maxVolatility < +data.volatility)
              maxVolatility = +data.volatility;
            std_data[data.Ticker] = {
              name: data.Ticker,
              company: companyName,
              value: +(Math.abs(data.volatility) * 100).toFixed(2),
              realValue: +(data.volatility * 100).toFixed(2),
              country: data.country_hq_name,
              industry: data.industry_fs,
              sector: data.sector_fs,
              type,
            };
          }
        },
      );

      this.setColors(return_data, minReturn * 100, maxReturn * 100);
      this.setColors(std_data, minVolatility * 100, maxVolatility * 100);

      this.treeMapData = [
        { name: "Annualized Return", data: Object.values(return_data) },
        { name: "Annualized Volatility", data: Object.values(std_data) },
      ];
    }
  }

  onDateChange(prop) {
    this.filter[prop] = this.filter[prop].format("YYYY-MM-DD");
  }

  updatePageWithBenchmark() {
    const portfolioStart = new Date(this.portfolio.start_date).valueOf();
    const portfolioEnd = new Date(this.portfolio.end_date).valueOf();
    const d = new Date(portfolioEnd);
    const dateOfTheYear = moment.utc(d).startOf("year").unix() * 1000;
    const portfolioCum = this.plotData?.cumulative_returns?.Portfolio;
    if (!portfolioCum?.length) {
      return;
    }
    const last_return_date = portfolioCum.slice(-1)[0][0];

    const last_return_date_1month =
      moment.utc(last_return_date).subtract(1, "month").unix() * 1000;
    if (this.benchmark.cumulative_returns?.length) {
      const copy = structuredClone(
        this.benchmark.cumulative_returns.filter(function (e) {
          return e.date >= +portfolioStart && e.date <= +portfolioEnd;
        }),
      );

      // Short portfolios (< 1 year, or fully within the current year) lack the
      // historical anchors that the year-over-year metrics need. Return NaN in
      // those cases so consumers can detect "not enough data" instead of
      // crashing on `.value` of an undefined slice. The downstream consumer at
      // line ~998 already null-checks `benchmarkReturn.ytd` with `&&`.
      const pctChange = (latest: number, anchor: any): number =>
        anchor && anchor.value !== 0
          ? ((latest - anchor.value) / anchor.value) * 100
          : NaN;
      const cagr = (latest: number, anchor: any, years: number): number =>
        anchor && anchor.value !== 0 && years > 0
          ? (Math.pow(latest / anchor.value, 1 / years) - 1) * 100
          : NaN;
      const anchorBeforeYearsAgo = (years: number) =>
        copy
          .filter(function (e) {
            return (
              e.date >= (moment.utc(d).unix() - years * 365 * 3600 * 24) * 1000
            );
          })
          .slice(-252 * years)[0];

      const lastBenchmark = this.benchmark.cumulative_returns.slice(-1)[0];
      const mntAnchor = this.benchmark.cumulative_returns.filter(function (e) {
        return e.date >= last_return_date_1month;
      })[0];

      if (copy.length >= 2 && copy[0].value !== 0) {
        const copyLast = copy[copy.length - 1];
        const ytdAnchor = copy
          .filter(function (e) {
            return e.date < dateOfTheYear;
          })
          .slice(-1)[0];
        const timediff =
          (copyLast.date - copy[0].date) / (1000 * 60 * 60 * 24) / 365.25;
        const annualized = this.benchmark.annualized_return;

        this.benchmarkReturn = {
          mnt: pctChange(lastBenchmark.value, mntAnchor),
          totalReturn: pctChange(copyLast.value, copy[0]),
          avgAnn: annualized?.length
            ? annualized.reduce((p, c) => p + c[1], 0) / annualized.length
            : NaN,
          ytd: pctChange(copyLast.value, ytdAnchor),
          cagrY1: cagr(copyLast.value, copy[0], timediff),
          cagrY2: cagr(lastBenchmark.value, anchorBeforeYearsAgo(2), 2),
          cagrY3: cagr(lastBenchmark.value, anchorBeforeYearsAgo(3), 3),
          cagrY5: cagr(lastBenchmark.value, anchorBeforeYearsAgo(5), 5),
          cagrY10: cagr(lastBenchmark.value, anchorBeforeYearsAgo(10), 10),
        };
      }
    }
    this.initAnnualSharpeChart();

    if (
      this.selectedBenchmark &&
      typeof this.selectedBenchmark.id == "number"
    ) {
      this.benchmarkCalculating = false;
    }
  }

  reloadPortfolioCalculatedData() {
    this.service.viewItem(this.portfolio.id).subscribe((resp: any) => {
      if (resp.id) {
        this.portfolio.performance_summary = resp.performance_summary;
        this.portfolio.symbol_performance = resp.symbol_performance;
        this.benchmarkCalculating = false;
      }
    });
  }

  onBenchmarkSelect(benchmark) {
    if (benchmark && benchmark.id) {
      this.service.notificationService.open(
        "Benchmark changed. Please click save to apply.",
      );
      this.selectedBenchmark = benchmark;
      this.portfolio.benchmark = benchmark.name;
      this.editBenchmarkName = benchmark.name;
      this.benchmarkCalculating = true;
    } else {
      this.updatePageWithBenchmark();
    }
  }

  onCalculateReturnsClick() {
    if (!this.portfolio.start_date || !this.portfolio.end_date) {
      return this.service.notificationService.open(
        "Fill the start and end dates.",
      );
    }
    this.portfolio.needReload = true;
    this.handleReturnCalculationRequest("Your backtest is running");
  }

  onDrawdownDatechange(_event) {}

  // -- Card exports preserved ---------------------------------------------

  exportCumulativeReturnsChart(): void {
    const element = document.getElementById("overview-cumulative");
    this.handleCardExport(
      "cumulativeReturns",
      element,
      `cumulative-returns-${this.portfolio?.name || "export"}.jpeg`,
    );
  }
  exportAnnualPerformanceChart(): void {
    const element = document.getElementById("overview-annual");
    this.handleCardExport(
      "annualPerformance",
      element,
      `annual-performance-${this.portfolio?.name || "export"}.jpeg`,
    );
  }
  exportWeightsPerformanceChart(): void {
    const element = document.getElementById("overview-weights");
    this.handleCardExport(
      "weightsPerformance",
      element,
      `weights-performance-${this.portfolio?.name || "export"}.jpeg`,
    );
  }
  exportPortfolioConstituentsCard(): void {
    const element = document.getElementById("overview-constituents");
    this.handleCardExport(
      "portfolioConstituents",
      element,
      `portfolio-constituents-${this.portfolio?.name || "export"}.jpeg`,
    );
  }
  exportSummaryRiskContributionChart(): void {
    const element = document.getElementById("overview-risk-contrib");
    this.handleCardExport(
      "summaryRiskContribution",
      element,
      `summary-risk-contribution-${this.portfolio?.name || "export"}.jpeg`,
    );
  }

  onExportPdf(): void {
    let targetPortfolio = "efficient_portfolio_max_sharpe_ratio";

    if (this.optim) {
      targetPortfolio = this.optim.toString();
    } else if (
      this.optimizationData &&
      this.optimizationData.optim_parameters &&
      this.optimizationData.optim_parameters.optim_performance
    ) {
      targetPortfolio =
        this.optimizationData.optim_parameters.optim_performance;
    }

    this.dialog.open(PdfExportDialogComponent, {
      data: {
        portfolioId: this.portfolio.id,
        portfolioName: this.portfolio.name,
        targetPortfolio,
      },
      maxWidth: "1200px",
      minWidth: "1100px",
    });
  }

  // -- Formatters ---------------------------------------------------------

  fmtPct(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return (v * 100).toFixed(d) + "%";
  }
  fmtNum(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return v.toFixed(d);
  }
  fmtMoney(v: number | null | undefined): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    if (Math.abs(v) >= 1_000_000) return "$" + (v / 1_000_000).toFixed(2) + "T";
    if (Math.abs(v) >= 1_000) return "$" + (v / 1_000).toFixed(2) + "B";
    return "$" + v.toFixed(0) + "M";
  }
  fmtDeltaPct = (v: number | null | undefined, d = 1): string => {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    const sign = v >= 0 ? "+" : "";
    return sign + (v * 100).toFixed(d) + "%";
  };
}
